import { PartnerDealStage } from './partner-deal.response';

/** O que aconteceu numa entrada da linha do tempo. Mesma grafia do enum do backend. */
export type PartnerDealTimelineEventType =
  | 'StageChanged'
  | 'PreApproved'
  | 'Rejected'
  | 'RejectionReverted'
  /** O cliente terminou de assinar o NDA + SCR (card 3281, T01); vem junto da mudança de etapa. */
  | 'ClientTermSigned'
  /** Parceiro ou Legatus criaram o acesso do cliente (card 3461); `notes` traz "nome (e-mail)". */
  | 'ClientAccessInvited'
  /** Pedido de Kit Banco enviado pela aba Documentos (card 3753); `notes` traz os e-mails que receberam. */
  | 'BankKitRequested';

/** Motivo da rejeição, na grafia do backend. */
export type PartnerDealRejectionReason =
  | 'ForaDoPerfil'
  | 'RestricaoDeCredito'
  | 'ClienteJaAtendido'
  | 'InformacoesInsuficientes'
  | 'DesistenciaDoCliente'
  | 'FaturamentoAbaixoDoMinimo'
  | 'EndividamentoElevado'
  /** O comitê de crédito negou (card 3789). Só o servidor grava; a modal de rejeição não oferece. */
  | 'NegadoPeloComite'
  | 'Outro';

/**
 * Rótulos dos motivos, para a tela. A ORDEM desta lista é a ordem da modal de rejeição.
 *
 * Os dois critérios numéricos (faturamento e endividamento) vieram da reunião de 25/09/2026 (card
 * 3071), na discussão dos gates; a lista inteira segue sujeita à revisão do comercial. "Outro" fica
 * por último — o campo de texto aberto vale para qualquer motivo, não só para ele.
 */
export const PARTNER_DEAL_REJECTION_REASON_LABELS: Record<PartnerDealRejectionReason, string> = {
  FaturamentoAbaixoDoMinimo: 'Faturamento abaixo do mínimo',
  EndividamentoElevado: 'Endividamento elevado',
  RestricaoDeCredito: 'Restrição de crédito',
  ForaDoPerfil: 'Fora do perfil atendido',
  ClienteJaAtendido: 'Cliente já atendido',
  InformacoesInsuficientes: 'Informações insuficientes',
  DesistenciaDoCliente: 'Desistência do cliente',
  NegadoPeloComite: 'Negado pelo comitê de crédito',
  Outro: 'Outro',
};

/**
 * Motivos que só o servidor grava e que a modal de rejeição manual não oferece: `NegadoPeloComite`
 * sai da decisão do comitê de crédito (card 3789), com as justificativas de quem negou.
 */
export const PARTNER_DEAL_SYSTEM_REJECTION_REASONS: PartnerDealRejectionReason[] = [
  'NegadoPeloComite',
];

/** Um acontecimento na linha do tempo do deal. */
export interface PartnerDealTimelineEntryResponse {
  eventType: PartnerDealTimelineEventType;
  fromStage?: PartnerDealStage | null;
  toStage?: PartnerDealStage | null;
  occurredAt: string;
  rejectionReason?: PartnerDealRejectionReason | null;
  notes?: string | null;
  /**
   * Quanto tempo o deal ficou na etapa **nesta passagem**, no formato `d.hh:mm:ss` do .NET.
   *
   * Nulo na última mudança, que ainda está correndo. Não confundir com o total da etapa: uma
   * etapa visitada duas vezes tem duas entradas, cada uma com a sua duração.
   */
  durationInStage?: string | null;
  /**
   * Quem fez, já no texto que a tela mostra (card 3067). Vem resolvido do servidor porque depende
   * de quem pergunta: o time interno vê o nome da pessoa; para o parceiro vem **sempre nulo** (card
   * 3189: autoria é auditoria da Legatus). Nulo também quando não há usuário identificado.
   */
  performedBy?: string | null;
}

/** Tempo acumulado numa etapa. */
export interface PartnerDealStageDurationResponse {
  stage: PartnerDealStage;
  /** **Soma** de todas as passagens por esta etapa, no formato `d.hh:mm:ss`. */
  total: string;
  visits: number;
  isCurrent: boolean;
}

/**
 * A linha do tempo do deal, já calculada pelo servidor.
 *
 * ⚠️ Os tempos vêm prontos de propósito: a regra de "etapa visitada duas vezes soma as
 * passagens" mora no backend, para que o board e esta tela nunca divirjam.
 */
export interface PartnerDealTimelineResponse {
  entries: PartnerDealTimelineEntryResponse[];
  stageDurations: PartnerDealStageDurationResponse[];
  currentStageSince?: string | null;
  timeInCurrentStage?: string | null;
  /**
   * As etapas do funil de quem vê, em ordem, com o estado de cada uma (card 3189). É o que o
   * stepper desenha. Para o parceiro, sem as etapas internas.
   */
  stages?: PartnerDealStageProgressResponse[];
}

/**
 * Em que pé está uma etapa (card 3189). Sai da linha do tempo, não da posição da etapa atual:
 * - `Done`: o deal passou por ela e saiu, mesmo que depois tenha voltado para trás;
 * - `Skipped`: vem antes da atual e o deal nunca passou por ela (pulada no arrasto, pendente);
 * - `Current`: a etapa de agora;
 * - `Upcoming`: à frente, ainda não percorrida.
 */
export type PartnerDealStageProgress = 'Done' | 'Skipped' | 'Current' | 'Upcoming';

/** O estado de uma etapa do funil para o deal. */
export interface PartnerDealStageProgressResponse {
  stage: PartnerDealStage;
  progress: PartnerDealStageProgress;
  /** Quando o deal saiu desta etapa pela última vez. Nulo quando não há registro. */
  completedAt?: string | null;
}
